import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  getTheme,
  setTheme as setThemeInStore,
  subscribe,
  type Theme,
} from "@/lib/theme/themeStore";

export type { Theme };

type ThemeCtx = {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
};

const Ctx = createContext<ThemeCtx | null>(null);

/**
 * Thin shell over `themeStore` — the store owns the DOM class, the
 * localStorage write and the cross-tab subscription, so imperative callers
 * (logout teardown, post-login hydration) and React never disagree.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSyncExternalStore(subscribe, getTheme, getTheme);

  const setTheme = useCallback((t: Theme) => {
    setThemeInStore(t);
  }, []);

  const toggle = useCallback(() => {
    setThemeInStore(getTheme() === "dark" ? "light" : "dark");
  }, []);

  const value = useMemo<ThemeCtx>(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTheme must be used inside <ThemeProvider>");
  return c;
}
